import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import { HtmlEditor, MessageMedium } from 'modules/graphql/types';
import { TemplateEditViews, UnselectedMessageMedium } from 'modules/templateEditor/common/types';
import { useEffect, useMemo } from 'react';
import { usePrevious } from 'utils/hooks/usePrevious';

// EditSMS
import { PreviewWithDataCore as SMSPreviewWithDataCore } from 'modules/templateEditor/EditSMS/PreviewWithData';
import { SettingsEditorCore as SMSSettingsEditorCore } from 'modules/templateEditor/EditSMS/Settings';
import { PreviewPane as SMSPreviewPane } from 'modules/templateEditor/EditSMS/preview/PreviewPane';

// EditEmail
import { ContentCore as EmailContentCore } from 'modules/templateEditor/EditEmail/Content';
import { PreviewWithDataCore as EmailPreviewWithDataCore } from 'modules/templateEditor/EditEmail/PreviewWithData';
import { SettingsEditorCore as EmailSettingsEditorCore } from 'modules/templateEditor/EditEmail/Settings';
import { PreviewPane as EmailPreviewPane } from 'modules/templateEditor/EditEmail/preview/PreviewPane';

// WebPush
import { PreviewWithDataCore as WebPushPreviewWithDataCore } from 'modules/templateEditor/EditWebPush/PreviewWithData';
import { SettingsEditorCore as WebPushSettingsEditorCore } from 'modules/templateEditor/EditWebPush/Settings';
import { PreviewPane as WebPushPreviewPane } from 'modules/templateEditor/EditWebPush/preview/PreviewPane';

// Push
import { PreviewWithDataCore as PushPreviewWithDataCore } from 'modules/templateEditor/EditPush/PreviewWithData';
import { SettingsEditorCore as PushSettingsEditorCore } from 'modules/templateEditor/EditPush/Settings';
import { PreviewPane as PushPreviewPane } from 'modules/templateEditor/EditPush/preview/PreviewPane';

// InApp
import { ContentCore as InAppContentCore } from 'modules/templateEditor/EditInApp/Content';
import { PreviewWithDataCore as InAppPreviewWithDataCore } from 'modules/templateEditor/EditInApp/PreviewWithData';
import { SettingsEditorCore as InAppSettingsEditorCore } from 'modules/templateEditor/EditInApp/Settings';
import { PreviewPane as InAppPreviewPane } from 'modules/templateEditor/EditInApp/preview/PreviewPane';

// Embedded
import { PreviewPane as EmbeddedPreviewPane } from 'modules/templateEditor/EditEmbedded/preview/PreviewPane';
import { PreviewWithDataCore as EmbeddedPreviewWithDataCore } from 'modules/templateEditor/EditEmbedded/preview/PreviewWithData';
import { SettingsEditorCore as EmbeddedSettingsEditorCore } from 'modules/templateEditor/EditEmbedded/settings/Settings';

// WhatsApp
import { PreviewPane as WhatsAppPreviewPane } from 'modules/templateEditor/EditWhatsApp/preview/PreviewPane';
import { PreviewWithDataCore as WhatsAppPreviewWithDataCore } from 'modules/templateEditor/EditWhatsApp/PreviewWithData';
import { SettingsEditorCore as WhatsAppSettingsEditorCore } from 'modules/templateEditor/EditWhatsApp/Settings';

import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { InStudioView } from '../common/enums';
import { useInStudioTakeoverContext } from '../context';

type EditorComponentType = {
  [mesageMediumKey in MessageMedium]?: {
    [editViewKey in TemplateEditViews]?: React.FunctionComponent<React.PropsWithChildren<unknown>>;
  };
};
type NodeEditorPreviewComponentType = {
  [mesageMediumKey in MessageMedium]?: React.FunctionComponent<React.PropsWithChildren<unknown>>;
};

/*
When you add a new template editor message medium type, we need to create a test for each of the imported view to
verify that there will be no regressions if the owning team makes any modifications.  This pattern is important for this
integration to work successfully.
*/

export const getEditorComponents = (medium: MessageMedium, view: TemplateEditViews) => {
  const mediumViews: EditorComponentType = {
    [MessageMedium.SMS]: {
      [TemplateEditViews.settings]: SMSSettingsEditorCore,
      [TemplateEditViews.preview]: SMSPreviewWithDataCore,
    },
    [MessageMedium.Email]: {
      [TemplateEditViews.settings]: EmailSettingsEditorCore,
      [TemplateEditViews.preview]: EmailPreviewWithDataCore,
      [TemplateEditViews.content]: EmailContentCore,
    },
    [MessageMedium.WebPush]: {
      [TemplateEditViews.settings]: WebPushSettingsEditorCore,
      [TemplateEditViews.preview]: WebPushPreviewWithDataCore,
    },
    [MessageMedium.Push]: {
      [TemplateEditViews.settings]: PushSettingsEditorCore,
      [TemplateEditViews.preview]: PushPreviewWithDataCore,
    },
    [MessageMedium.InApp]: {
      [TemplateEditViews.settings]: InAppSettingsEditorCore,
      [TemplateEditViews.preview]: InAppPreviewWithDataCore,
      [TemplateEditViews.content]: InAppContentCore,
    },
    [EmbeddedMessage]: {
      [TemplateEditViews.settings]: EmbeddedSettingsEditorCore,
      [TemplateEditViews.preview]: EmbeddedPreviewWithDataCore,
    },
    [WhatsApp]: {
      [TemplateEditViews.settings]: WhatsAppSettingsEditorCore,
      [TemplateEditViews.preview]: WhatsAppPreviewWithDataCore,
    },
  };
  const mediumView = mediumViews?.[medium];
  // if (!mediumView)
  //   throw new Error('In studio editing of templates requires a medium to be selected.');
  return mediumView?.[view] ?? mediumView?.[TemplateEditViews.settings];
};

export const getNodeEditorPreviewComponents = (medium: MessageMedium) => {
  const mediumViews: NodeEditorPreviewComponentType = {
    [MessageMedium.SMS]: SMSPreviewPane,
    [MessageMedium.Push]: PushPreviewPane,
    [MessageMedium.WebPush]: WebPushPreviewPane,
    [MessageMedium.Email]: EmailPreviewPane,
    [MessageMedium.InApp]: InAppPreviewPane,
    [EmbeddedMessage]: EmbeddedPreviewPane,
    [WhatsApp]: WhatsAppPreviewPane,
  };
  const mediumView = mediumViews?.[medium];
  // if (!mediumView)
  //   throw new Error('In studio editing of templates requires a medium to be selected.');
  return mediumView;
};

type EditorViewLoader = {
  isLoading: boolean;
  loadedView: React.FunctionComponent<React.PropsWithChildren<unknown>>;
};

export const useEditorViewLoader = (inStudioView: InStudioView): EditorViewLoader => {
  const { view } = useInStudioTakeoverContext();
  const { medium, isLoading: templateEditorLoading } = useTemplateQueryContext();
  const { contentEditor, liveTemplate, setDragAndDropInitValue, setDragAndDropInitTs } =
    useEditableFieldsContext();
  const loadedView = useMemo(
    () =>
      inStudioView === InStudioView.Preview
        ? getNodeEditorPreviewComponents(medium as MessageMedium)
        : getEditorComponents(medium as MessageMedium, view as TemplateEditViews),
    [medium, view, inStudioView],
  ) as React.FunctionComponent<React.PropsWithChildren<unknown>>;
  const isLoading =
    templateEditorLoading ||
    medium === UnselectedMessageMedium.Unselected ||
    !liveTemplate?.templateId;
  const viewPrev = usePrevious(view);

  /**
   * when the drag-and-drop (Bee) editor is being used, and you switch to the content editor view,
   * make sure the initValue is current.
   */
  const shouldInitializeDragAndDropEditor =
    contentEditor === HtmlEditor.Bee && view === TemplateEditViews.content && view !== viewPrev;

  useEffect(() => {
    /**
     * calling setDragAndDropInitValue without a param implies
     * that the latest value should be used.
     */
    if (shouldInitializeDragAndDropEditor) {
      setDragAndDropInitValue();
      setDragAndDropInitTs();
    }
  }, [shouldInitializeDragAndDropEditor]);

  return { isLoading, loadedView };
};
